import { FormEvent, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../api/auth";
import type { ApiError } from "../api/client";

type TFn = (k: string) => string;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <CenteredCard>
        <h1 style={titleStyle}>{t("reset.title")}</h1>
        <p>{t("errors.tokenInvalid")}</p>
        <Link to="/forgot-password" style={linkStyle}>{t("reset.requestNew")}</Link>
      </CenteredCard>
    );
  }

  const checks = policyChecks(t);
  const policyHints = checks.filter((c) => !c.test(password));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("errors.passwordsMismatch"));
      return;
    }
    if (policyHints.length > 0) {
      setError(t("errors.passwordPolicy"));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      navigate("/login?reset=ok", { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(translateError(apiErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CenteredCard>
      <h1 style={titleStyle}>{t("reset.title")}</h1>
      {error && <div style={errorStyle}>{error}</div>}
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <label style={labelStyle}>
          <span>{t("reset.newPassword")}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          <span>{t("reset.confirmPassword")}</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            style={inputStyle}
          />
        </label>

        <ul style={{ fontSize: 13, color: "#6b7280", margin: 0, paddingLeft: 16 }}>
          {checks.map((c) => (
            <li key={c.label} style={{ color: c.test(password) ? "#15803d" : "#6b7280" }}>
              {c.test(password) ? "✓ " : "• "} {c.label}
            </li>
          ))}
        </ul>

        <button type="submit" disabled={submitting} style={btnStyle}>
          {submitting ? t("reset.submitting") : t("reset.submit")}
        </button>
      </form>
    </CenteredCard>
  );
}

function policyChecks(t: TFn): { label: string; test: (s: string) => boolean }[] {
  return [
    { label: t("reset.policy.length"), test: (s) => s.length >= 12 },
    { label: t("reset.policy.upper"), test: (s) => /[A-Z]/.test(s) },
    { label: t("reset.policy.lower"), test: (s) => /[a-z]/.test(s) },
    { label: t("reset.policy.digit"), test: (s) => /\d/.test(s) },
  ];
}

function translateError(err: ApiError, t: TFn): string {
  switch (err.code) {
    case "TOKEN_EXPIRED":
      return t("errors.tokenExpired");
    case "TOKEN_USED":
      return t("errors.tokenUsed");
    case "TOKEN_INVALID":
      return t("errors.tokenInvalid");
    case "PASSWORD_BREACHED":
      return t("errors.passwordBreached");
    case "PASSWORD_TOO_SHORT":
    case "PASSWORD_NEEDS_UPPERCASE":
    case "PASSWORD_NEEDS_LOWERCASE":
    case "PASSWORD_NEEDS_DIGIT":
      return t("errors.passwordPolicy");
    default:
      return err.message || t("errors.generic");
  }
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return <div style={shellStyle}><div style={cardStyle}>{children}</div></div>;
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f4f6f8",
  padding: 16,
};
const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 420,
  background: "#fff",
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const titleStyle: React.CSSProperties = { margin: 0, color: "#0b3a6f", fontSize: 24 };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 14 };
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  fontSize: 15,
};
const btnStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "#0b3a6f",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const linkStyle: React.CSSProperties = { color: "#0b3a6f", fontSize: 14 };
const errorStyle: React.CSSProperties = {
  padding: 12,
  background: "#fef2f2",
  color: "#b91c1c",
  borderRadius: 6,
  fontSize: 14,
};
